import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Clock, CheckCircle, XCircle, Eye, RotateCcw, X, AlertTriangle, ArrowRightLeft, RefreshCw, Filter, Square, CheckSquare, EyeOff, Group, Loader2, GitBranch, MessageSquare, Download, ChevronDown, ShieldX } from 'lucide-react';
import { getDriftEvents, getDriftEvent, getDriftStats, resolveDriftEvent, ignoreDriftEvent, rejectDriftEvent, reopenDriftEvent, deleteDriftEvent, deleteDriftEvents, getContracts, exportDriftEvent, getExportAttempts } from '../api';
import Modal from './Modal';
import ExportStatusBadge, { getOverallExportStatus } from './ExportStatusBadge';
import ExportAttemptsModal from './ExportAttemptsModal';

const PAGE_SIZE = 20; // Number of items per page

const DriftInbox = ({ addToast, onReviewClick, refreshTrigger }) => {
    // Data state
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState({ total: 0, pending: 0, resolved: 0, ignored: 0 });
    const [contracts, setContracts] = useState([]);

    // Pagination state
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Filter state
    const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem('driftInbox_statusFilter') || 'all');
    const [hideIgnored, setHideIgnored] = useState(() => {
        const stored = localStorage.getItem('driftInbox_hideIgnored');
        return stored !== null ? stored === 'true' : true;
    });

    // Selection state
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Modal state
    const [showResolveModal, setShowResolveModal] = useState(false);

    // Form state
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [selectedContractId, setSelectedContractId] = useState('');
    const [batchDeleting, setBatchDeleting] = useState(false);
    const [groupBy, setGroupBy] = useState(() => localStorage.getItem('driftInbox_groupBy') || 'source');

    // Intersection observer for infinite scroll
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    // Export tracking state
    const [showExportAttemptsModal, setShowExportAttemptsModal] = useState(false);
    const [selectedExportEvent, setSelectedExportEvent] = useState(null);
    // Resolution action state
    const [autoVersion, setAutoVersion] = useState(false);
    const [notifySlack, setNotifySlack] = useState(true);
    const [resolving, setResolving] = useState(false);
    // Export state
    const [exportEnabled, setExportEnabled] = useState(false);
    const [exportType, setExportType] = useState('target');
    const [exporting, setExporting] = useState(null); // holds event ID while exporting

    // Deep link state
    const [deepLinkEventId, setDeepLinkEventId] = useState(null);
    const [highlightedEventId, setHighlightedEventId] = useState(null);
    const eventRefs = useRef({});

    // Undo grace period state
    const [pendingDeletions, setPendingDeletions] = useState(new Set());
    const deletionTimeoutsRef = useRef({});

    // Filter events based on hideIgnored setting
    const filteredEvents = useMemo(() => {
        const visible = events.filter(e => !pendingDeletions.has(e.id));
        if (!hideIgnored) return visible;
        return visible.filter(e => e.status !== 'ignored');
    }, [events, hideIgnored, pendingDeletions]);

    // Group events by source_name (similar to vendor grouping in ContractsManager)
    const groupedEvents = useMemo(() => {
        const groups = {};
        filteredEvents.forEach(event => {
            const sourceKey = event.source_name || 'Unknown Source';
            if (!groups[sourceKey]) {
                groups[sourceKey] = [];
            }
            groups[sourceKey].push(event);
        });
        // Sort: named sources first (alphabetically), Unknown Source last
        const sortedKeys = Object.keys(groups).sort((a, b) => {
            if (a === 'Unknown Source') return 1;
            if (b === 'Unknown Source') return -1;
            return a.localeCompare(b);
        });
        return sortedKeys.map(source => ({
            source,
            events: groups[source]
        }));
    }, [filteredEvents]);

    // Persist filter preferences to localStorage
    useEffect(() => { localStorage.setItem('driftInbox_statusFilter', statusFilter); }, [statusFilter]);
    useEffect(() => { localStorage.setItem('driftInbox_hideIgnored', String(hideIgnored)); }, [hideIgnored]);
    useEffect(() => { localStorage.setItem('driftInbox_groupBy', groupBy); }, [groupBy]);

    // Reset and fetch when filters change
    useEffect(() => {
        setEvents([]);
        setCursor(null);
        setHasMore(true);
        fetchData(true);
    }, [statusFilter, hideIgnored]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Don't fire when user is typing in an input, textarea, or select
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            // Don't fire when a modal is open
            if (showResolveModal || showExportAttemptsModal) return;

            // Shift + A: toggle select all
            if (e.shiftKey && e.key === 'A') {
                e.preventDefault();
                if (selectedIds.size === filteredEvents.length && filteredEvents.length > 0) {
                    setSelectedIds(new Set());
                } else {
                    setSelectedIds(new Set(filteredEvents.map(ev => ev.id)));
                }
                return;
            }

            // Escape: clear selection
            if (e.key === 'Escape') {
                if (selectedIds.size > 0) {
                    e.preventDefault();
                    setSelectedIds(new Set());
                }
                return;
            }

            // R (no modifier): refresh
            if (e.key === 'r' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
                e.preventDefault();
                handleRefresh();
                return;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [filteredEvents, selectedIds, showResolveModal, showExportAttemptsModal]);

    // Initial data fetch
    useEffect(() => {
        fetchContractsAndStats();
    }, []);

    // Parse URL query parameters for deep linking
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const eventId = params.get('event');
        if (eventId) {
            setDeepLinkEventId(parseInt(eventId, 10));
        }
    }, []);

    // Handle deep linking when events are loaded or deepLinkEventId changes
    useEffect(() => {
        if (!deepLinkEventId || events.length === 0) return;

        const handleDeepLink = async () => {
            // Check if event is already in the list
            const eventExists = events.some(e => e.id === deepLinkEventId);

            if (eventExists) {
                // Event found - scroll to it and highlight
                scrollToEvent(deepLinkEventId);
            } else {
                // Event not in current list - fetch it specifically
                try {
                    const event = await getDriftEvent(deepLinkEventId);
                    if (event) {
                        // Add event to the list and then scroll to it
                        setEvents(prev => {
                            // Avoid duplicates
                            if (prev.some(e => e.id === event.id)) return prev;
                            return [event, ...prev];
                        });
                        // Wait for render then scroll
                        setTimeout(() => scrollToEvent(deepLinkEventId), 100);
                    }
                } catch (error) {
                    console.error('Failed to fetch deep-linked event:', error);
                    addToast('Could not find the specified drift event', 'error');
                }
            }

            // Clear URL parameter to avoid re-triggering on refresh
            clearUrlParameter();
            // Clear deep link event ID to prevent re-processing
            setDeepLinkEventId(null);
        };

        handleDeepLink();
    }, [deepLinkEventId, events.length]);

    // Scroll to a specific event and highlight it
    const scrollToEvent = (eventId) => {
        const element = eventRefs.current[eventId];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedEventId(eventId);
            // Remove highlight after 3 seconds
            setTimeout(() => setHighlightedEventId(null), 3000);
        }
    };

    // Clear the URL query parameter without reloading
    const clearUrlParameter = () => {
        const url = new URL(window.location);
        url.searchParams.delete('event');
        window.history.replaceState({}, '', url);
        setDeepLinkEventId(null);
    };

    // Infinite scroll observer
    useEffect(() => {
        if (loading || loadingMore || !hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    fetchData(false);
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => observer.disconnect();
    }, [cursor, hasMore, loading, loadingMore, statusFilter]);

    const fetchContractsAndStats = async () => {
        try {
            const [statsData, contractsData] = await Promise.all([
                getDriftStats(),
                getContracts()
            ]);
            setStats(statsData);
            setContracts(contractsData);
        } catch (error) {
            console.error('Failed to fetch stats/contracts:', error);
        }
    };

    const fetchData = async (reset = false) => {
        if (reset) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            const currentCursor = reset ? null : cursor;
            const response = await getDriftEvents({
                status: statusFilter === 'all' ? undefined : statusFilter,
                cursor: currentCursor,
                limit: PAGE_SIZE
            });

            if (reset) {
                setEvents(response.items || []);
            } else {
                setEvents(prev => [...prev, ...(response.items || [])]);
            }

            setCursor(response.next_cursor);
            setHasMore(response.has_more);

            // Update stats in background
            getDriftStats().then(setStats).catch(console.error);
        } catch (error) {
            console.error('Failed to fetch drift events:', error);
            addToast('Failed to load drift events', 'error');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleRefresh = () => {
        setEvents([]);
        setCursor(null);
        setHasMore(true);
        fetchData(true);
    };

    // Refetch events when refreshTrigger arrives from the App (e.g. after resolving/rejecting in Comparator)
    useEffect(() => {
        if (refreshTrigger) {
            handleRefresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    const handleResolve = async () => {
        if (!selectedEvent) return;
        setResolving(true);
        try {
            const result = await resolveDriftEvent(selectedEvent.id, {
                resolution_notes: resolutionNotes || null,
                resolution_contract_id: selectedContractId ? parseInt(selectedContractId) : null,
                actions: {
                    auto_version: autoVersion,
                    notify_slack: notifySlack
                },
                export: {
                    enabled: exportEnabled,
                    export_type: exportType
                }
            });

            // Trigger browser download if export was requested
            if (exportEnabled) {
                try {
                    await exportDriftEvent(selectedEvent.id, exportType);
                } catch (exportErr) {
                    console.error('Export download failed:', exportErr);
                    addToast('Resolved, but export download failed', 'warning');
                }
            }

            // Show action result feedback
            const actions = result.actions_executed || [];
            const successCount = actions.filter(a => a.success).length;
            const failCount = actions.filter(a => !a.success).length;

            if (failCount > 0) {
                const failedNames = actions.filter(a => !a.success).map(a => a.action).join(', ');
                addToast(`Resolved with ${failCount} action warning(s): ${failedNames}`, 'warning');
            } else if (successCount > 0) {
                addToast(`Resolved — ${successCount} action(s) completed`, 'success');
            } else {
                addToast('Drift event resolved', 'success');
            }

            setShowResolveModal(false);
            setResolutionNotes('');
            setSelectedContractId('');
            setAutoVersion(false);
            setNotifySlack(true);
            setExportEnabled(false);
            setExportType('target');
            setSelectedEvent(null);
            setResolving(false);
            // Optimistic update - update local state immediately
            setEvents(prev => prev.map(e =>
                e.id === selectedEvent.id
                    ? { ...e, status: 'resolved', resolution_notes: resolutionNotes || null }
                    : e
            ));
            // Update stats in background
            getDriftStats().then(setStats).catch(console.error);
        } catch (error) {
            addToast('Failed to resolve event', 'error');
            setResolving(false);
        }
    };

    const handleExport = async (event, type = 'target') => {
        setExporting(event.id);
        try {
            const result = await exportDriftEvent(event.id, type);
            addToast(`Downloaded: ${result.filename}`, 'success');
        } catch (error) {
            console.error('Export failed:', error);
            addToast('Failed to export file', 'error');
        } finally {
            setExporting(null);
        }
    };

    const handleIgnore = async (event) => {
        try {
            await ignoreDriftEvent(event.id);
            addToast('Drift event ignored', 'info');
            // Optimistic update
            setEvents(prev => prev.map(e =>
                e.id === event.id ? { ...e, status: 'ignored' } : e
            ));
            getDriftStats().then(setStats).catch(console.error);
        } catch (error) {
            addToast('Failed to ignore event', 'error');
        }
    };

    const handleReject = async (event) => {
        try {
            await rejectDriftEvent(event.id, {
                rejection_reason: 'Rejected from Drift Inbox',
                notify_slack: true
            });
            addToast('Drift rejected — baseline unchanged', 'success');
            // Optimistic update
            setEvents(prev => prev.map(e =>
                e.id === event.id ? { ...e, status: 'rejected' } : e
            ));
            getDriftStats().then(setStats).catch(console.error);
        } catch (error) {
            addToast('Failed to reject drift event', 'error');
        }
    };

    const handleReopen = async (event) => {
        try {
            await reopenDriftEvent(event.id);
            addToast('Drift event reopened', 'info');
            // Optimistic update
            setEvents(prev => prev.map(e =>
                e.id === event.id ? { ...e, status: 'pending', resolution_notes: null, resolved_at: null } : e
            ));
            getDriftStats().then(setStats).catch(console.error);
        } catch (error) {
            addToast('Failed to reopen event', 'error');
        }
    };

    const handleDelete = useCallback((event) => {
        // Optimistic UI hide
        setPendingDeletions(prev => new Set(prev).add(event.id));

        const timeoutId = setTimeout(async () => {
            try {
                await deleteDriftEvent(event.id);
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(event.id);
                    return next;
                });
                // Optimistic update - remove event
                setEvents(prev => prev.filter(e => e.id !== event.id));
                getDriftStats().then(setStats).catch(console.error);
            } catch (error) {
                addToast('Failed to delete event', 'error');
                // Revert optimistic hide
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(event.id);
                    return next;
                });
            }
        }, 2000);

        deletionTimeoutsRef.current[event.id] = timeoutId;

        // Also remove from selectedIds if present immediately
        const newSelected = new Set(selectedIds);
        if (newSelected.has(event.id)) {
            newSelected.delete(event.id);
            setSelectedIds(newSelected);
        }

        addToast('Drift event deleted', 'success', {
            duration: 2000,
            action: {
                label: 'Undo',
                onClick: () => {
                    clearTimeout(deletionTimeoutsRef.current[event.id]);
                    delete deletionTimeoutsRef.current[event.id];
                    setPendingDeletions(prev => {
                        const next = new Set(prev);
                        next.delete(event.id);
                        return next;
                    });
                    addToast('Deletion undone', 'info');
                }
            }
        });
    }, [selectedIds, addToast]);

    const handleBatchDelete = useCallback(() => {
        if (selectedIds.size === 0) return;

        const idsArray = Array.from(selectedIds);

        // Optimistic UI hide
        setPendingDeletions(prev => {
            const next = new Set(prev);
            idsArray.forEach(id => next.add(id));
            return next;
        });

        // Store current selection for undo
        const previousSelection = new Set(selectedIds);
        setSelectedIds(new Set()); // clear selection visually

        const batchId = `batch-${Date.now()}`;

        const timeoutId = setTimeout(async () => {
            try {
                setBatchDeleting(true);
                await deleteDriftEvents(idsArray);
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    idsArray.forEach(id => next.delete(id));
                    return next;
                });
                // Optimistic update
                setEvents(prev => prev.filter(e => !idsArray.includes(e.id)));
                getDriftStats().then(setStats).catch(console.error);
            } catch (error) {
                addToast('Failed to delete events', 'error');
                // Revert optimistic hide
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    idsArray.forEach(id => next.delete(id));
                    return next;
                });
            } finally {
                setBatchDeleting(false);
            }
        }, 2000);

        deletionTimeoutsRef.current[batchId] = timeoutId;

        addToast(`Deleted ${idsArray.length} drift events`, 'success', {
            duration: 2000,
            action: {
                label: 'Undo',
                onClick: () => {
                    clearTimeout(deletionTimeoutsRef.current[batchId]);
                    delete deletionTimeoutsRef.current[batchId];
                    setPendingDeletions(prev => {
                        const next = new Set(prev);
                        idsArray.forEach(id => next.delete(id));
                        return next;
                    });
                    setSelectedIds(previousSelection); // Restore selection
                    addToast('Batch deletion undone', 'info');
                }
            }
        });
    }, [selectedIds, addToast]);

    const toggleSelect = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === filteredEvents.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredEvents.map(e => e.id)));
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return (
                    <span className="flex items-center gap-1.5 px-2 py-1 bg-yellow-900/40 text-[#cccccc] text-xs font-medium rounded-lg">
                        <Clock size={12} className="text-yellow-500" />
                        Pending
                    </span>
                );
            case 'resolved':
                return (
                    <span className="flex items-center gap-1.5 px-2 py-1 bg-green-900/40 text-[#cccccc] text-xs font-medium rounded-lg">
                        <CheckCircle size={12} className="text-emerald-500" />
                        Resolved
                    </span>
                );
            case 'ignored':
                return (
                    <span className="flex items-center gap-1.5 px-2 py-1 bg-[#3c3c3c] text-[#cccccc] text-xs font-medium rounded-lg">
                        <XCircle size={12} className="text-gray-400" />
                        Ignored
                    </span>
                );
            case 'rejected':
                return (
                    <span className="flex items-center gap-1.5 px-2 py-1 bg-red-900/40 text-[#cccccc] text-xs font-medium rounded-lg">
                        <ShieldX size={12} className="text-red-500" />
                        Rejected
                    </span>
                );
            default:
                return null;
        }
    };

    const getDriftSummary = (event) => {
        const parts = [];
        if (event.rename_count > 0) parts.push(`${event.rename_count} rename${event.rename_count !== 1 ? 's' : ''}`);
        if (event.addition_count > 0) parts.push(`${event.addition_count} addition${event.addition_count !== 1 ? 's' : ''}`);
        if (event.removal_count > 0) parts.push(`${event.removal_count} removal${event.removal_count !== 1 ? 's' : ''}`);
        if (event.change_count > 0) parts.push(`${event.change_count} change${event.change_count !== 1 ? 's' : ''}`);
        return parts.length > 0 ? parts.join(', ') : 'No changes detected';
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-white flex items-center gap-2">
                        <AlertTriangle size={20} className="text-indigo-400" />
                        Drift Inbox
                    </h2>
                    <p className="text-[#808080] mt-1">
                        Track and manage schema drift detections
                    </p>
                </div>

                {/* Stats Pills */}
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-2 px-3 py-1.5 bg-yellow-900/40 text-[#cccccc] text-sm font-medium rounded-lg">
                        <Clock size={14} className="text-yellow-500" />
                        {stats.pending} Pending
                    </span>
                    <span className="flex items-center gap-2 px-3 py-1.5 bg-green-900/40 text-[#cccccc] text-sm font-medium rounded-lg">
                        <CheckCircle size={14} className="text-emerald-500" />
                        {stats.resolved} Resolved
                    </span>
                    <div className="px-3 py-1.5 bg-[#3c3c3c] text-[#cccccc] text-sm font-medium rounded-lg flex items-center gap-2">
                        <XCircle size={14} className="text-gray-400" />
                        {stats.ignored} Ignored
                    </div>
                    {(stats.rejected || 0) > 0 && (
                        <span className="flex items-center gap-2 px-3 py-1.5 bg-red-900/40 text-[#cccccc] text-sm font-medium rounded-lg">
                            <ShieldX size={14} className="text-red-500" />
                            {stats.rejected} Rejected
                        </span>
                    )}
                </div>
            </div>

            {/* Filter Bar */}
            <div className="flex items-center justify-between gap-4 p-3 bg-[#252526] rounded-lg border border-[#3c3c3c]">
                {/* Left side - Filters */}
                <div className="flex items-center gap-3">
                    <Filter size={16} className="text-gray-500" />
                    <div className="flex bg-[#3c3c3c] rounded-lg p-1 border border-[#4c4c4c]">
                        {['all', 'pending', 'resolved', 'ignored', 'rejected'].map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setStatusFilter(filter)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all capitalize ${statusFilter === filter
                                    ? 'bg-blue-600 text-white'
                                    : 'text-[#808080] hover:bg-[#4c4c4c]'
                                    }`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setHideIgnored(!hideIgnored)}
                        className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-all ${hideIgnored
                            ? 'bg-[#4c4c4c] text-white'
                            : 'text-[#808080] hover:bg-[#4c4c4c]'
                            }`}
                        title={hideIgnored ? 'Hiding ignored events' : 'Showing ignored events'}
                    >
                        {hideIgnored ? <EyeOff size={14} /> : <Eye size={14} />}
                        {hideIgnored ? 'Hiding Ignored' : 'Show Ignored'}
                    </button>

                    {/* Group By Dropdown */}
                    <div className="relative">
                        <select
                            value={groupBy}
                            onChange={(e) => setGroupBy(e.target.value)}
                            className="appearance-none bg-[#3c3c3c] border border-[#4c4c4c] text-xs font-medium rounded-lg px-3 py-1.5 pr-8 text-[#808080] cursor-pointer hover:bg-[#4c4c4c] transition-colors"
                        >
                            <option value="source">Group by Source</option>
                            <option value="none">No Grouping</option>
                        </select>
                        <Group size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center gap-3">
                    {selectedIds.size > 0 && (
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-[#cccccc]">
                                {selectedIds.size} selected
                            </span>
                            <button
                                onClick={handleBatchDelete}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                            >
                                <X size={14} />
                                Delete Selected
                            </button>
                            <button
                                onClick={() => setSelectedIds(new Set())}
                                className="px-3 py-1.5 text-xs font-medium text-[#cccccc] hover:bg-[#3c3c3c] rounded-lg transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    )}
                    <button
                        onClick={handleRefresh}
                        className={`p-2 text-gray-500 hover:bg-[#3c3c3c] rounded-lg transition-colors ${loading ? 'animate-spin' : ''}`}
                        title="Refresh"
                    >
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            {/* Events List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <RefreshCw size={24} className="animate-spin text-blue-500" />
                </div>
            ) : filteredEvents.length === 0 ? (
                <div className="text-center py-12 bg-[#252526] rounded-lg border border-[#3c3c3c]">
                    <ArrowRightLeft size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-[#cccccc] mb-2">No drift events</h3>
                    <p className="text-sm text-[#808080]">
                        {statusFilter === 'all'
                            ? 'Run a comparison to detect schema drift'
                            : `No ${statusFilter} events found`}
                    </p>
                </div>
            ) : groupBy === 'source' ? (
                <div className="space-y-6">
                    {groupedEvents.map((group) => (
                        <div key={group.source}>
                            {/* Source Separator with Select All */}
                            <div className="flex items-center gap-3 mb-3">
                                <div className="h-[2px] flex-1 bg-white/20"></div>
                                <span className="text-sm font-bold uppercase tracking-widest px-4 text-white">
                                    {group.source}
                                </span>
                                <div className="h-[2px] flex-1 bg-white/20"></div>
                            </div>
                            {/* Select All for this group */}
                            <button
                                onClick={() => {
                                    const groupIds = new Set(group.events.map(e => e.id));
                                    if (selectedIds.size >= group.events.length && group.events.every(e => selectedIds.has(e.id))) {
                                        // Deselect all in group
                                        const newSelected = new Set(selectedIds);
                                        group.events.forEach(e => newSelected.delete(e.id));
                                        setSelectedIds(newSelected);
                                    } else {
                                        // Select all in group
                                        const newSelected = new Set(selectedIds);
                                        group.events.forEach(e => newSelected.add(e.id));
                                        setSelectedIds(newSelected);
                                    }
                                }}
                                className="flex items-center gap-1.5 text-xs text-[#808080] hover:text-[#cccccc] mb-2 ml-5"
                            >
                                {group.events.length > 0 && group.events.every(e => selectedIds.has(e.id)) ? (
                                    <CheckSquare size={14} className="text-blue-600" />
                                ) : (
                                    <Square size={14} />
                                )}
                                Select All ({group.events.length})
                            </button>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {group.events.map((event) => (
                                    <div
                                        key={event.id}
                                        ref={(el) => { eventRefs.current[event.id] = el; }}
                                        className={`relative group transition-all rounded-xl border ${selectedIds.has(event.id)
                                            ? 'ring-2 ring-blue-500 border-blue-500'
                                            : highlightedEventId === event.id
                                                ? 'ring-2 ring-orange-500 border-orange-500 animate-pulse'
                                                : event.status === 'pending'
                                                    ? 'border-yellow-700'
                                                    : 'border-[#3c3c3c]'
                                            } bg-[#252526]`}
                                    >
                                        {/* Top Right Badges & Actions */}
                                        <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                                            {event.status === 'resolved' && (
                                                <ExportStatusBadge
                                                    status={getOverallExportStatus(event.export_attempts)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedExportEvent(event);
                                                        setShowExportAttemptsModal(true);
                                                    }}
                                                />
                                            )}
                                            {getStatusBadge(event.status)}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(event);
                                                }}
                                                className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-[#3c3c3c] transition-all"
                                                title="Delete"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>

                                        <div className="p-4">
                                            <div className="flex items-start gap-3">
                                                {/* Checkbox */}
                                                <button
                                                    onClick={() => toggleSelect(event.id)}
                                                    className="mt-1 flex-shrink-0"
                                                >
                                                    {selectedIds.has(event.id) ? (
                                                        <CheckSquare size={18} className="text-blue-600" />
                                                    ) : (
                                                        <Square size={18} className="text-gray-400" />
                                                    )}
                                                </button>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start gap-3 mb-1">

                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-baseline gap-2 flex-wrap mb-1">
                                                                <h3 className="font-semibold text-white text-base truncate">
                                                                    {event.source_name}
                                                                </h3>
                                                                <span className="text-gray-400 text-sm">→</span>
                                                                <h3 className="font-semibold text-white text-base truncate pr-16">
                                                                    {event.target_name}
                                                                </h3>
                                                            </div>

                                                            {/* Stats row */}
                                                            <div className="flex items-center gap-3 text-xs text-gray-500 mb-0">
                                                                <span className="flex items-center gap-1">
                                                                    <div className={`w-1.5 h-1.5 rounded-full ${event.status === 'pending' ? 'bg-yellow-500' : event.status === 'resolved' ? 'bg-green-500' : event.status === 'rejected' ? 'bg-red-500' : 'bg-gray-500'}`}></div>
                                                                    {getDriftSummary(event)}
                                                                </span>
                                                                <span>•</span>
                                                                <span className="flex items-center gap-1">
                                                                    <Clock size={12} />
                                                                    {formatDate(event.created_at)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-[#333] ml-13">
                                                {onReviewClick && (
                                                    <button
                                                        onClick={() => onReviewClick(event)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-blue-400 hover:bg-blue-900/40"
                                                    >
                                                        <Eye size={13} />
                                                        Review
                                                    </button>
                                                )}

                                                {event.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedEvent(event);
                                                                setShowResolveModal(true);
                                                            }}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-emerald-400 hover:bg-emerald-900/40"
                                                        >
                                                            <CheckCircle size={13} />
                                                            Resolve
                                                        </button>
                                                        <button
                                                            onClick={() => handleIgnore(event)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-[#4c4c4c] text-gray-400 hover:bg-[#4c4c4c]"
                                                        >
                                                            <XCircle size={13} />
                                                            Ignore
                                                        </button>
                                                        <button
                                                            onClick={() => handleReject(event)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-red-400 hover:bg-red-900/40"
                                                        >
                                                            <ShieldX size={13} />
                                                            Reject
                                                        </button>
                                                    </>
                                                )}

                                                {(event.status === 'resolved' || event.status === 'ignored' || event.status === 'rejected') && (
                                                    <button
                                                        onClick={() => handleReopen(event)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-orange-400 hover:bg-orange-900/40"
                                                    >
                                                        <RotateCcw size={13} />
                                                        Reopen
                                                    </button>
                                                )}

                                                {/* Standalone Export */}
                                                <button
                                                    onClick={() => handleExport(event)}
                                                    disabled={exporting === event.id}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-[#4c4c4c] text-gray-300 hover:bg-[#4c4c4c] disabled:opacity-50"
                                                    title="Download accepted schema"
                                                >
                                                    {exporting === event.id ? (
                                                        <Loader2 size={13} className="animate-spin" />
                                                    ) : (
                                                        <Download size={13} />
                                                    )}
                                                    Export
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Select All Header */}
                    <div className="col-span-full">
                        <button
                            onClick={() => {
                                if (selectedIds.size === filteredEvents.length) {
                                    setSelectedIds(new Set());
                                } else {
                                    setSelectedIds(new Set(filteredEvents.map(e => e.id)));
                                }
                            }}
                            className="flex items-center gap-2 text-sm text-white font-semibold flex items-center gap-2 group-hover:text-blue-400 transition-colors mb-2 ml-4"
                        >
                            {selectedIds.size === filteredEvents.length && filteredEvents.length > 0 ? (
                                <CheckSquare size={16} className="text-blue-600" />
                            ) : (
                                <Square size={16} />
                            )}
                            Select All ({filteredEvents.length})
                        </button>
                    </div>
                    {filteredEvents.map((event) => (
                        <div
                            key={event.id}
                            ref={(el) => { eventRefs.current[event.id] = el; }}
                            className={`relative group transition-all rounded-xl border ${selectedIds.has(event.id)
                                ? 'ring-2 ring-blue-500 border-blue-500'
                                : highlightedEventId === event.id
                                    ? 'ring-2 ring-orange-500 border-orange-500 animate-pulse'
                                    : event.status === 'pending'
                                        ? 'border-yellow-700'
                                        : 'border-[#3c3c3c]'
                                } bg-[#252526]`}
                        >
                            {/* Top Right Badges & Actions */}
                            <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                                {event.status === 'resolved' && (
                                    <ExportStatusBadge
                                        status={getOverallExportStatus(event.export_attempts)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedExportEvent(event);
                                            setShowExportAttemptsModal(true);
                                        }}
                                    />
                                )}
                                {getStatusBadge(event.status)}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedEvent(event);
                                        setShowDeleteModal(true);
                                    }}
                                    className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-[#3c3c3c] transition-all"
                                    title="Delete"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="p-4">
                                <div className="flex items-start gap-3">
                                    {/* Checkbox */}
                                    <button
                                        onClick={() => toggleSelect(event.id)}
                                        className="mt-1 flex-shrink-0"
                                    >
                                        {selectedIds.has(event.id) ? (
                                            <CheckSquare size={18} className="text-blue-600" />
                                        ) : (
                                            <Square size={18} className="text-gray-400" />
                                        )}
                                    </button>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start gap-3 mb-1">


                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline gap-2 flex-wrap mb-1">
                                                    <h3 className="font-semibold text-white text-base truncate">
                                                        {event.source_name}
                                                    </h3>
                                                    <span className="text-gray-400 text-sm">→</span>
                                                    <h3 className="font-semibold text-white text-base truncate pr-16">
                                                        {event.target_name}
                                                    </h3>
                                                </div>

                                                {/* Stats row */}
                                                <div className="flex items-center gap-3 text-xs text-gray-500 mb-0">
                                                    <span className="flex items-center gap-1">
                                                        <div className={`w-1.5 h-1.5 rounded-full ${event.status === 'pending' ? 'bg-yellow-500' : event.status === 'resolved' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                                                        {getDriftSummary(event)}
                                                    </span>
                                                    <span>•</span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={12} />
                                                        {formatDate(event.created_at)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center flex-wrap gap-2 mt-3 pt-3 border-t border-[#3c3c3c] ml-13">
                                    <button
                                        onClick={() => {
                                            setSelectedEvent(event);
                                            onReviewClick(event);
                                        }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-blue-400 hover:bg-blue-900/40"
                                    >
                                        <Eye size={13} />
                                        Review
                                    </button>

                                    <button
                                        onClick={() => handleIgnore(event)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-[#4c4c4c] text-gray-400 hover:bg-[#4c4c4c]"
                                    >
                                        <XCircle size={13} />
                                        Ignore
                                    </button>

                                    <button
                                        onClick={() => handleReject(event)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-red-400 hover:bg-red-900/40"
                                    >
                                        <ShieldX size={13} />
                                        Reject
                                    </button>

                                    {(event.status === 'pending' || event.status === 'ignored') && (
                                        <button
                                            onClick={() => {
                                                setSelectedEvent(event);
                                                setShowResolveModal(true);
                                            }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-emerald-400 hover:bg-emerald-900/40"
                                        >
                                            <CheckCircle size={13} />
                                            Resolve
                                        </button>
                                    )}

                                    {(event.status === 'resolved' || event.status === 'ignored' || event.status === 'rejected') && (
                                        <button
                                            onClick={() => handleReopen(event)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-orange-400 hover:bg-orange-900/40"
                                        >
                                            <RotateCcw size={13} />
                                            Reopen
                                        </button>
                                    )}

                                    {/* Standalone Export */}
                                    <button
                                        onClick={() => handleExport(event)}
                                        disabled={exporting === event.id}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-[#4c4c4c] text-gray-300 hover:bg-[#4c4c4c] disabled:opacity-50"
                                        title="Download accepted schema"
                                    >
                                        {exporting === event.id ? (
                                            <Loader2 size={13} className="animate-spin" />
                                        ) : (
                                            <Download size={13} />
                                        )}
                                        Export
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Infinite Scroll Loading Indicator */}
            <div ref={loadMoreRef} className="py-6 flex flex-col items-center justify-center">
                {loadingMore && (
                    <div className="flex items-center gap-2 text-[#808080]">
                        <Loader2 size={20} className="animate-spin" />
                        <span className="text-sm">Loading more...</span>
                    </div>
                )}
                {!loadingMore && hasMore && !loading && (
                    <div className="flex items-center gap-1 text-[#606060] text-sm">
                        <ChevronDown size={16} />
                        <span>Scroll to load more</span>
                    </div>
                )}
                {!hasMore && events.length > 0 && !loading && (
                    <div className="text-[#606060] text-sm">
                        No more events
                    </div>
                )}
            </div>

            {/* Resolve Modal */}
            <Modal
                isOpen={showResolveModal}
                onClose={() => {
                    setShowResolveModal(false);
                    setSelectedEvent(null);
                    setResolutionNotes('');
                    setSelectedContractId('');
                    setAutoVersion(false);
                    setNotifySlack(true);
                    setExportEnabled(false);
                    setExportType('target');
                }}
                title="Resolve Drift Event"
                footer={
                    <>
                        <button
                            onClick={() => setShowResolveModal(false)}
                            disabled={resolving}
                            className="px-4 py-2 text-[#cccccc] hover:bg-[#3c3c3c] rounded-lg disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleResolve}
                            disabled={resolving}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {resolving ? (
                                <><Loader2 size={14} className="animate-spin" /> Resolving...</>
                            ) : exportEnabled ? (
                                <><Download size={14} /> Resolve & Send</>
                            ) : (
                                'Mark as Resolved'
                            )}
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-[#cccccc]">
                        Mark this drift event as resolved. Optionally add notes or link to a saved contract.
                    </p>
                    <div>
                        <label className="block text-sm font-medium text-[#808080] mb-1">
                            Resolution Notes (optional)
                        </label>
                        <textarea
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            placeholder="What was done to resolve this drift?"
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-[#cccccc] resize-none placeholder-[#606060]"
                            rows={3}
                        />
                    </div>
                    <div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#808080] mb-1">
                                Contract
                            </label>
                            <select
                                value={selectedContractId}
                                onChange={(e) => {
                                    setSelectedContractId(e.target.value);
                                    // Disable auto-version if no contract selected
                                    if (!e.target.value) setAutoVersion(false);
                                }}
                                className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white"
                            >
                                <option value="" className="text-[#808080]">-- Select a contract --</option>
                                {contracts.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Resolution Actions */}
                    <div>
                        <label className="block text-sm font-medium text-[#808080] mb-2">
                            Resolution Actions
                        </label>
                        <div className="space-y-1">
                            {/* Auto-version checkbox */}
                            <label className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${!selectedContractId ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#2a2a2a]'}`}>
                                <input
                                    type="checkbox"
                                    checked={autoVersion}
                                    onChange={(e) => setAutoVersion(e.target.checked)}
                                    disabled={!selectedContractId}
                                    className="mt-0.5 accent-green-500 w-4 h-4"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <GitBranch size={13} className="text-[#4ec9b0]" />
                                        <span className="text-sm text-[#cccccc]">Save as new contract version</span>
                                    </div>
                                    <p className="text-xs text-[#606060] mt-0.5">
                                        {selectedContractId
                                            ? 'Accept the drifted schema as the new baseline version'
                                            : 'Select a contract above to enable'}
                                    </p>
                                </div>
                            </label>

                            {/* Slack notification checkbox */}
                            <label className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-[#2a2a2a] cursor-pointer transition-colors">
                                <input
                                    type="checkbox"
                                    checked={notifySlack}
                                    onChange={(e) => setNotifySlack(e.target.checked)}
                                    className="mt-0.5 accent-green-500 w-4 h-4"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <MessageSquare size={13} className="text-[#569cd6]" />
                                        <span className="text-sm text-[#cccccc]">Notify via Slack</span>
                                    </div>
                                    <p className="text-xs text-[#606060] mt-0.5">
                                        Send resolution notification to configured Slack channel
                                    </p>
                                </div>
                            </label>
                        </div>
                        <p className="text-xs text-[#505050] mt-2 italic">
                            Audit report and log entry are always created automatically.
                        </p>
                    </div>

                    {/* Export Section */}
                    <div>
                        <label className="block text-sm font-medium text-[#808080] mb-2">
                            Export
                        </label>
                        <label className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-[#2a2a2a] cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={exportEnabled}
                                onChange={(e) => setExportEnabled(e.target.checked)}
                                className="mt-0.5 accent-green-500 w-4 h-4"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-1.5">
                                    <Download size={13} className="text-[#dcdcaa]" />
                                    <span className="text-sm text-[#cccccc]">Download file on resolve</span>
                                </div>
                                <p className="text-xs text-[#606060] mt-0.5">
                                    Export the schema or diff report as a file download
                                </p>
                            </div>
                        </label>

                        {exportEnabled && (
                            <div className="ml-9 mt-2 space-y-1.5">
                                {[
                                    { value: 'target', label: 'Accepted schema', desc: 'The drifted/updated version' },
                                    { value: 'source', label: 'Baseline schema', desc: 'The original contract version' },
                                    { value: 'diff', label: 'Diff report', desc: 'HTML comparison report' },
                                ].map(opt => (
                                    <label key={opt.value} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-[#2a2a2a] cursor-pointer transition-colors">
                                        <input
                                            type="radio"
                                            name="exportType"
                                            value={opt.value}
                                            checked={exportType === opt.value}
                                            onChange={() => setExportType(opt.value)}
                                            className="accent-green-500 w-3.5 h-3.5"
                                        />
                                        <div>
                                            <span className="text-sm text-[#cccccc]">{opt.label}</span>
                                            <span className="text-xs text-[#606060] ml-2">{opt.desc}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div >
            </Modal >

            {/* Removed Delete Modals */}

            {/* Export Attempts Modal */}
            <ExportAttemptsModal
                isOpen={showExportAttemptsModal}
                onClose={() => {
                    setShowExportAttemptsModal(false);
                    setSelectedExportEvent(null);
                }}
                driftEvent={selectedExportEvent}
                addToast={addToast}
            />
        </div >
    );
};

export default DriftInbox;
